import type { PublicDogEvent } from '@/types'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  eventTypeLabel,
  getParticipantsPhase,
  hasEntryStarted,
  isEntryClosed,
  isEntryOpen,
  isEntryUpcoming,
  isEventLive,
  isEventOver,
  isValidForEntry,
  localizedEventName,
} from '@/lib/event'
import { liveViewEnabled } from '@/lib/features'
import { Path } from '@/routeConfig'
import { CollapsibleEvent } from '../../components/CollapsibleEvent'
import LinkButton from '../../components/LinkButton'
import { languageAtom } from '../../state'
import { EventInfo } from './EventInfo'
import { EventStateInfo } from './EventStateInfo'
import { EventPlaces } from './eventTableRow/EventPlaces'

interface Props {
  readonly event: PublicDogEvent
  readonly odd?: boolean
}

const EventHeader = ({ event }: Props) => {
  const { t } = useTranslation()
  const language = useAtomValue(languageAtom)
  const cancelledSx = event.state === 'cancelled' ? { textDecoration: 'line-through' } : undefined

  const infoText = useMemo(() => {
    if (isEntryOpen(event)) return t('dateFormat.datespan', { end: event.entryEndDate, start: event.entryStartDate })
    if (isEventOver(event)) return t('event.states.confirmed_eventOver')

    const participantsPhase = getParticipantsPhase(event)
    if (participantsPhase) return t(`event.states.${participantsPhase}`)

    if (isEntryClosed(event)) return t('event.states.confirmed_entryClosed_info')

    if (event.state !== 'tentative' && isEntryUpcoming(event))
      return (
        <div style={{ position: 'relative' }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              bottom: '20px',
              fontSize: '10px',
              lineHeight: '12px',
              position: 'absolute',
              right: 0,
              textAlign: 'right',
            }}
          >
            {t('entryOpens')}
          </Typography>
          {t('dateFormat.date', { date: event.entryStartDate })}
        </div>
      )

    return null
  }, [event, t])

  const showPlaces = useMemo((): boolean => hasEntryStarted(event) && isValidForEntry(event.state), [event])

  return (
    <>
      <Grid container columnSpacing={1} size={12}>
        <Grid
          size="grow"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {event.organizer.name}
          </Typography>
        </Grid>
        <Grid
          offset="auto"
          sx={{
            display: { sm: 'block', xs: 'none' },
          }}
        >
          {showPlaces ? <EventPlaces event={event} /> : null}
        </Grid>
      </Grid>
      <Grid
        container
        columnSpacing={1}
        size={{
          sm: 'auto',
          xs: 12,
        }}
        sx={cancelledSx}
      >
        <Grid>{t('dateFormat.datespan', { end: event.endDate, start: event.startDate })}</Grid>
        <Grid
          size={{
            sm: 'auto',
            xs: 'grow',
          }}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
          }}
        >
          {eventTypeLabel(event)}
        </Grid>
        <Grid
          offset="auto"
          sx={{
            display: { sm: 'none' },
          }}
        >
          {showPlaces ? <EventPlaces event={event} /> : null}
        </Grid>
      </Grid>
      <Grid
        container
        columnSpacing={1}
        size={{
          sm: 'grow',
          xs: 12,
        }}
      >
        <Grid sx={cancelledSx}>{event.location}</Grid>
        <Grid
          size={{
            sm: 'auto',
            xs: 'grow',
          }}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...cancelledSx,
            textWrap: 'nowrap',
          }}
        >
          {localizedEventName(event, language)}
        </Grid>
        <Grid
          size="auto"
          offset={{
            sm: 'auto',
          }}
          sx={{
            alignContent: 'center',
          }}
        >
          <Typography variant="body2" component="div">
            {isEntryOpen(event) ? (
              <LinkButton to={Path.register(event)} text={t('register')} sx={{ pr: 0 }} />
            ) : (
              <EventStateInfo event={event} live={liveViewEnabled && isEventLive(event)} text={infoText} />
            )}
          </Typography>
        </Grid>
      </Grid>
    </>
  )
}

export const EventListItem = ({ event, odd }: Props) => (
  <CollapsibleEvent
    cancelled={event.state === 'cancelled'}
    eventId={event.id}
    header={<EventHeader event={event} />}
    odd={odd}
  >
    <EventInfo event={event}></EventInfo>
  </CollapsibleEvent>
)
